import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ClickableTextPanel } from "@/Common/Components/ClickableText/ClickableTextPanel";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { SimpleTextareaResizable } from "@/Common/Components/Inputs/SimpleTextareaResizable";
import { DocumentTreeClassControl } from "@/Common/Helpers/DocumentTreeClassControl";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { Guid } from "@/Common/Models/Guid";
import { DocumentClass } from "@/Common/Resources/Lookups/Enums/DocumentClasses";
import { EditDocumentClassesPopup } from "@/Documents/Components/InboundFax/EditDocumentClassesPopup";
import { InboundFaxesMailboxActions } from "@/Documents/Components/InboundFax/InboundFaxesMailboxActions";
import { InboundFaxIndexingFormModel } from "@/Documents/Models/InboundFax/InboundFaxIndexingFormModel";
import { InboundFaxNotes } from "@/Documents/Models/InboundFax/InboundFaxNotes";
import { InboundFaxTriageFormSettingsModel } from "@/Documents/Models/Settings/InboundFaxTriageFormSettings";
import { InboundFaxNoteList } from "./InboundFaxNoteList";

import "./InboundFaxIndexingForm.css";
import "./InboundFaxTriageForm.css";

import ClinicalNoteIcon from "@/Documents/Assets/icons/triage/classes/clinical note.svg";
import ConsultationReportsIcon from "@/Documents/Assets/icons/triage/classes/consultation reports.svg";
import FormsIcon from "@/Documents/Assets/icons/triage/classes/forms.svg";
import MedicationAndPrescriptionsIcon from "@/Documents/Assets/icons/triage/classes/medication and prescriptions.svg";
import OtherIcon from "@/Documents/Assets/icons/triage/classes/other.svg";
import RequestsIcon from "@/Documents/Assets/icons/triage/classes/requests.svg";
import ResultsIcon from "@/Documents/Assets/icons/triage/classes/results.svg";
import ScannedPaperRecordIcon from "@/Documents/Assets/icons/triage/classes/scanned paper record.svg";
import SummaryIcon from "@/Documents/Assets/icons/triage/classes/summary.svg";

interface InboundFaxTriageFormProps {
    onChange: (formData: InboundFaxIndexingFormModel) => void;
    onClassClick: (classLookupId: (Guid | string), subClassLookupId: (Guid | string)) => void;
    inboundFaxIndexingFormModel: InboundFaxIndexingFormModel;
    onShowReturnFaxToSenderPopup: () => void;
    reportClassLookupId: string;
    reportSubClassLookupId: string;
    faxNotes: InboundFaxNotes[];
}

class InboundFaxTriageFormState {
    public showEditClassesPopup: boolean = false;
    public isNoteClickableTextShown: boolean = false;
    public classes: DocumentClassModel[] = [];
    public selectedClasses: (Guid | string)[] = [];

    public constructor(init?: Partial<InboundFaxTriageFormState>) {
        Object.assign(this, init);
    }
}

class InboundFaxTriageForm extends BaseReactComponent<InboundFaxTriageFormProps, InboundFaxTriageFormState> {
    private documentClasses: DocumentClassModel[] = DocumentTreeClassControl.GetDocumentClasses().toArray();
    private defaultDocumentClasses: (Guid | string)[] =
        [
            DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.MedicationAndPrescriptions).LookupId,
            DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.Results).LookupId,
            DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.ConsultationReports).LookupId,
            DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.Other).LookupId
        ];
    private classIconByLookupId = {
        [DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.Summary).LookupId]: <SummaryIcon />,
        [DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.ClinicalNote).LookupId]: <ClinicalNoteIcon />,
        [DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.ConsultationReports).LookupId]: <ConsultationReportsIcon />,
        [DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.Results).LookupId]: <ResultsIcon />,
        [DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.Forms).LookupId]: <FormsIcon />,
        [DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.MedicationAndPrescriptions).LookupId]: <MedicationAndPrescriptionsIcon />,
        [DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.Requests).LookupId]: <RequestsIcon />,
        [DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.Other).LookupId]: <OtherIcon />,
        [DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.ScannedPaperRecord).LookupId]: <ScannedPaperRecordIcon />
    };

    public constructor(props: InboundFaxTriageFormProps) {
        super(props);

        const visibleClasses = this.getVisibleClassIds();
        this.state = new InboundFaxTriageFormState({
            classes: this.documentClasses.filter(f => visibleClasses.includes(f.LookupId))
        });
    }

    public render(): JSX.Element {
        return (
            <div id="inbound-fax-indexing-form">

                {this.state.showEditClassesPopup && (
                    <EditDocumentClassesPopup
                        selectedClasses={this.state.selectedClasses}
                        onCancel={() => this.onCloseTriageClassEditPopup()}
                        onSave={updatedSelectClasses => this.onSaveHandler(updatedSelectClasses)}
                    />
                )}
                <div className="inbound-fax-form-row">
                    <a className="clickable-text-link" onClick={() => this.props.onShowReturnFaxToSenderPopup()}>{cacheStore.GetString("ReturnToSenderLabel")}</a>
                    <CheckboxControl
                        label={cacheStore.GetString("PrioritizeLabel")}
                        checked={this.props.inboundFaxIndexingFormModel.isUrgent}
                        onChange={(isChecked: boolean) => this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, isUrgent: isChecked })}
                    />
                </div>
                <InboundFaxNoteList faxNotes={this.props.faxNotes} />
                <div className="inbound-fax-form-row">
                    <label htmlFor="inboundFaxTriageForm_NotesField">
                        {cacheStore.GetString("NotesLabel")}
                    </label>

                    <SimpleTextareaResizable
                        id="inboundFaxTriageForm_NotesField"
                        className="form-control textarea-resize-vertical"
                        rows={3}
                        value={this.props.inboundFaxIndexingFormModel?.notes ?? ""}
                        onChange={event => this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, notes: event.target.value })}
                        maxLength={1000}
                        minHeight="90px"
                        autoFocus={true}
                    />
                    {this.state.isNoteClickableTextShown && (
                        <div className="clickable-text-container">
                            <ClickableTextPanel
                                cssClass="text-helper-container"
                                context="InboundFaxNotes"
                                inputElementId="inboundFaxTriageForm_NotesField"
                                onCloseEvent={() => this.toggleClickableNotesVisibility()}
                            />
                        </div>
                    )}
                    <a className="clickable-text-link" onClick={() => this.toggleClickableNotesVisibility()}> {cacheStore.GetString("SelectFromListLabel")}</a>
                </div>

                <div className="inbound-fax-form-row">
                    <CheckboxControl
                        label={cacheStore.GetString("InboundFax_AssociateClassToFaxLabel")}
                        checked={this.props.inboundFaxIndexingFormModel.shouldAssignClassFaxNumber}
                        onChange={(isChecked: boolean) => this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, shouldAssignClassFaxNumber: isChecked })}
                        labelClassName="inboundFax-label-no-bold"
                    />
                </div>

                <div className="inbound-fax-form-row">
                    <label>
                        {cacheStore.GetString("ClassLabel")}
                    </label>
                    <div className="triage-form-class-selector">
                        {this.state.classes?.map(c => (
                            <button
                                key={c.LookupId}
                                onClick={() => this.onClickTriageButton(c.LookupId, c.LookupCode)}
                                className={`class-button ${c.LookupId === this.props.reportClassLookupId ? "selected" : ""}`}
                                disabled={c.LookupId === this.props.reportClassLookupId}
                            >
                                <div className="class-icon" title={c.LookupCode}>
                                    {this.classIconByLookupId[c.LookupId]}
                                </div>
                                <span className="class-text">{cacheStore.GetLocalizedValueByLookupId(c.LookupId)}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <a className="clickable-text-link" onClick={() => this.onClickEditAvailableClassesLabel()}>{cacheStore.GetString("EditAvailableClassesLabel")}</a>
                </div>

            </div>
        );
    }

    private onSaveHandler(updatedSelectClasses: (Guid | string)[]): void {
        let refreshClasses = this.saveFilterSettings(updatedSelectClasses);
        if (!refreshClasses.any())
            refreshClasses = this.defaultDocumentClasses;

        this.mutateState(s => {
            s.showEditClassesPopup = false;
            s.classes = this.documentClasses.filter(f => refreshClasses.includes(f.LookupId));
        }, () => this.props.onChange(this.props.inboundFaxIndexingFormModel));
    }

    private saveFilterSettings(updatedSelectClasses: (Guid | string)[]): (Guid | string)[] {
        const defaultDocumentClassesCount = this.defaultDocumentClasses.length;
        const result = (updatedSelectClasses.length == defaultDocumentClassesCount)
            ? updatedSelectClasses.
                filter(selectedId => this.defaultDocumentClasses.
                    some(defaultId => defaultId === selectedId)
                ).length == defaultDocumentClassesCount
            : false;

        const triageFormSettingsModel: InboundFaxTriageFormSettingsModel = new InboundFaxTriageFormSettingsModel();
        triageFormSettingsModel.SelectedDocumentClassIds = result ? [] : updatedSelectClasses;
        InboundFaxesMailboxActions.saveTriageFormSettings(triageFormSettingsModel);

        return updatedSelectClasses;
    }

    private onCloseTriageClassEditPopup(): void {
        this.mutateState(s => s.showEditClassesPopup = false);
    }

    private onClickEditAvailableClassesLabel(): void {
        this.mutateState(s => {
            s.selectedClasses = [...this.state.classes.map(x => x.LookupId)];
            s.showEditClassesPopup = true;
        });
    }

    private getVisibleClassIds(): (Guid | string)[] {
        return cacheStore.UserSettings?.InboundFaxTriageFormSettings?.SelectedDocumentClassIds ?? this.defaultDocumentClasses;
    }

    private toggleClickableNotesVisibility(): void {
        this.mutateState(s => s.isNoteClickableTextShown = !s.isNoteClickableTextShown);
    }

    private onClickTriageButton(selectedClassLookupId: string, selectedClassLookupCode: string): void {
        const selectedSubClassLookupId = DocumentTreeClassControl.getDefaultSubclassForIndexingFax(selectedClassLookupCode);
        this.props.onClassClick(selectedClassLookupId, selectedSubClassLookupId);
    }
}

export { InboundFaxTriageForm };
